<script lang="ts">
import { defineComponent, reactive } from 'vue'
import FollowSnsForFooter from '@/components/cta/FollowSnsForFooter.vue'
import stateInitializer from '@/models/stateInitializer'

export default defineComponent({
  name: 'FooterGlobal',

  components: {
    FollowSnsForFooter,
  },

  props: {
    hasFollowSns: {
      type: Boolean,
      default: true,
    },
  },

  setup() {
    interface State {
      navigations: FooterNavigation[],
    }
    const state: State = reactive({
      navigations: [],
    })
    stateInitializer(state, 'footer')

    return {
      // data
      state,
    }
  }
})
</script>

<template>
  <footer
    ref="footer"
    class="footer">

    <FollowSnsForFooter v-if="hasFollowSns" />

    <section class="f-section__footerNavigation">
      <div class="f-section__footerNavigation--body">
        <div class="l-cols__footerNavigation">
          <div
            v-for="(navigation, i) in state.navigations"
            :key="i"
            :class="[
              'l-cols__footerNavigation--col',
              { 'is-narrow': navigation.isNarrowColumn },
            ]">
            <div class="o-list__navigationCategory">
              <dl class="o-list__navigationCategory--unit">
                <dt class="o-list__navigationCategory--term">
                  {{ navigation.header }}
                </dt>
                <dd class="o-list__navigationCategory--description">
                  <div
                    :class="[
                      'o-list__footerNavigation',
                      navigation.isNarrowColumn ? 'is-col1' : 'is-col2',
                    ]">
                    <ul class="o-list__footerNavigation--items">
                      <li
                        v-for="(item, j) in navigation.items"
                        :key="j"
                        class="o-list__footerNavigation--item">
                        <div class="o-action__footerNavigation">
                          <a
                            class="o-action__footerNavigation--item"
                            :href="item.to"
                            :target="`${ item.isTargetBlank ? '_blank' : '_self' }`">
                            {{ item.label }}
                          </a>
                        <!-- /.o-action__footerNavigation --></div>
                      </li>
                    </ul>
                  <!-- /.o-list__footerNavigation --></div>
                </dd>
              </dl>
            <!-- /.o-list__navigationCategory --></div>
          <!-- /.l-cols__footerNavigation col --></div>
        <!-- /.l-cols__footerNavigation --></div>
      <!-- /.f-section__footerNavigation body --></div>
    <!-- /.f-section__footerNavigation --></section>

    <section class="f-section__footerLegal">
      <div class="f-section__footerLegal--body">
        <div class="o-label__legal">
          <p class="o-label__legal--item is-notice">
            記載されている会社名および商品・サービス名（ロゴマーク等を含む）は、各社の商標または各権利者の登録商標です。<br>
            All company names, product and service names, and logos are trademarks or registered trademarks of their respective owners.
          </p>

          <p class="o-label__legal--item is-copyright">
            BizHint（ビズヒント）- <span class="span u-text__block">クラウド活用と生産性向上の専門サイト</span>
          </p>
        <!-- /.o-label__legal --></div>
      <!-- /.f-section__footerLegal body --></div>
    <!-- /.f-section__footerLegal --></section>
  </footer>
</template>

<style lang="sass" scoped>
// foundation
.f-section__footerNavigation
  position: relative
  padding: 2.5em 0 3em
  background-color: rgba($bg-white, 1)
  border-top: 1px solid $border-x-light-gray

  & &--body
    width: 100%
    max-width: #{$global-width-pc}px
    margin: 0 auto
    padding: 0 #{$global-prop-pc}px

  +breakpoint
    display: none

.f-section__footerLegal
  $root: &
  position: relative
  padding:
    top: 1em
    bottom: changeSizePx2Em(40)
  background-color: rgba($bg-white, 1)

  & &--body
    width: 100%
    max-width: #{$global-width-pc}px
    margin: 0 auto
    padding: 0 #{$global-prop-pc}px

  +breakpoint
    background-color: transparent

    #{$root}--body
      padding: 0 #{$global-prop-sp}px

// layout
.l-cols__footerNavigation
  $root: &
  display: flex

  $grid: math.div(100%, 5)
  & &--col
    width: $grid * 2

    &.is-narrow
      width: $grid * 1

  +breakpoint($max-media: tablet)
    display: block

    #{$root}--col
      width: auto
      margin-top: 1.5em

      &:first-child
        margin-top: 0

      &.is-narrow
        width: auto

// object
.o-list__navigationCategory
  $root: &
  height: 100%

  & &--unit
    height: 100%
    display: flex
    flex-direction: column

  & &--term
    line-height: 1
    font:
      size: $font-small
      weight: bold

  & &--description
    flex-grow: 1
    margin-top: 1em
    padding-left: 1em
    border-left: 1px solid $border-x-light-gray

  +breakpoint($max-media: tablet)
    #{$root}--description
      padding-left: 1em
      border-left: none

.o-list__footerNavigation
  $root: &

  & &--items
    display: flex
    flex-wrap: wrap

  // PC 表示
  +breakpoint($max: false, $min-media: tablet)
    #{$root}--item
      margin-top: 0.9em

    @for $i from 1 through 2
      &.is-col#{$i}
        #{$root}--item
          width: math.div(100%, $i)

          &:nth-child(-n+#{$i})
            margin-top: 0

  // Tablet 表示
  +breakpoint($max-media: tablet)
    #{$root}--items
      margin-top: -1em

    #{$root}--item
      width: auto
      position: relative
      margin:
        top: 1em
        right: 0.8em
      padding-right: 0.8em

      &::after
        content: ""
        width: 0
        height: 0.5em
        display: block
        position: absolute
        top: 50%
        right: 0
        transform: translateY(-50%)
        border-right: 1px solid $border-x-light-gray

      &:last-child
        margin-right: 0
        padding-right: 0

        &::after
          display: none

    &[class*="is-col"]
      #{$root}--item
        width: auto

.o-action__footerNavigation
  line-height: 1

  & &--item
    color: $font-black
    font-size: $font-small
    text-decoration: none

    +mouseover
      text-decoration: underline

.o-label__legal
  $root: &

  & &--item
    line-height: 1.4
    color: $font-light-gray
    font-size: $font-x-small
    margin-top: 1em

    &:first-child
      margin-top: 0

    &.is-copyright
      display: none

  +breakpoint
    #{$root}--item
      &.is-copyright
        display: block
        text-align: center
</style>
